import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";
import { formatDate } from "@/utils/media";
import SectionHeading from "@/components/feature/SectionHeading";

interface NewsHighlightProps {
  items: NewsItem[];
}

export default function NewsHighlight({ items }: NewsHighlightProps) {
  const [featured, ...rest] = items;

  if (!featured) return null;

  return (
    <section className="mx-auto max-w-[1280px] px-4 md:px-6">
      <SectionHeading
        title="影视资讯精选"
        subtitle="行业动态 · 影评 · 剧集观察"
        icon="ri-newspaper-line"
        moreTo="/news"
      />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Link
          to={`/news/${featured.id}`}
          className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
        >
          <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
            <img
              src={featured.cover}
              alt={featured.title}
              title={`${featured.title} - 麻豆精品`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="p-5">
            <span className="inline-block rounded-md bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
              {featured.category}
            </span>
            <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-xl">
              {featured.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
              {featured.summary}
            </p>
            <p className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" aria-hidden="true"></i>
                {featured.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {formatDate(featured.publishedAt)}
              </span>
            </p>
          </div>
        </Link>

        <ul className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
          {rest.map((item) => (
            <li key={item.id}>
              <Link
                to={`/news/${item.id}`}
                className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100 md:p-5"
              >
                <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-20 md:w-32">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 麻豆精品`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-medium text-accent-700">{item.category}</span>
                  <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-1 hidden text-xs text-foreground-500 sm:block">
                    {item.summary}
                  </p>
                  <p className="mt-1.5 text-xs text-foreground-400">{formatDate(item.publishedAt)}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}